import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { getGoogleMapsApiKey } from '../config/api';
import { Search } from 'lucide-react';

interface SimpleGoogleMapProps {
  latitude: number;
  longitude: number;
  title: string;
  address: string;
  className?: string;
  height?: string;
  showFullAddress?: boolean;
  enableSearch?: boolean;
  openInfoByDefault?: boolean;
}

const SimpleGoogleMap: React.FC<SimpleGoogleMapProps> = ({
  latitude,
  longitude,
  title,
  address,
  className = '',
  height = 'h-96',
  showFullAddress = true,
  enableSearch = false,
  openInfoByDefault = true
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [searchResults, setSearchResults] = useState<google.maps.Marker[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Default to Kolkata if coordinates are invalid
  const validLat = latitude && !isNaN(latitude) && latitude !== 0 ? latitude : 22.5726;
  const validLng = longitude && !isNaN(longitude) && longitude !== 0 ? longitude : 88.3639;
  const hasValidCoordinates = latitude !== 0 && longitude !== 0 && !isNaN(latitude) && !isNaN(longitude);

  // Initialize search functionality
  const initializeSearch = (map: google.maps.Map, google: typeof window.google) => {
    if (!searchInputRef.current) return;

    // Create autocomplete
    const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
      fields: ['place_id', 'geometry', 'name', 'formatted_address'],
      types: ['establishment', 'geocode']
    });

    autocompleteRef.current = autocomplete;

    // Listen for place selection
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        console.log('No details available for input: ' + place.name);
        return;
      }

      // Clear previous search results
      searchResults.forEach(marker => marker.setMap(null));
      setSearchResults([]);

      // Create marker for searched location
      const searchMarker = new google.maps.Marker({
        position: place.geometry.location,
        map: map,
        title: place.name || 'Searched Location',
        animation: google.maps.Animation.DROP,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="#3b82f6"/>
              <circle cx="12" cy="12" r="6" fill="white"/>
              <circle cx="12" cy="12" r="3" fill="#3b82f6"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 24)
        }
      });

      // Create info window for search result
      const searchInfoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 180px; font-family: system-ui, sans-serif;">
            <h3 style="margin: 0 0 6px 0; color: #1f2937; font-size: 14px; font-weight: 600;">
              ${place.name || 'Searched Location'}
            </h3>
            <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 12px;">${place.formatted_address || ''}</p>
            <div style="margin-top: 8px;">
              <button onclick="window.openInGoogleMaps(${place.geometry.location.lat()}, ${place.geometry.location.lng()})" 
                      style="background: #3b82f6; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; margin-right: 6px;">
                Open Maps
              </button>
              <button onclick="window.getDirections('${place.formatted_address || place.name}', ${place.geometry.location.lat()}, ${place.geometry.location.lng()})" 
                      style="background: #10b981; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px;">
                Directions
              </button>
            </div>
          </div>
        `
      });

      // Add click listener to search marker
      searchMarker.addListener('click', () => {
        searchInfoWindow.open(map, searchMarker);
      });

      // Add to search results
      setSearchResults([searchMarker]);

      // Center map on searched location
      map.setCenter(place.geometry.location);
      map.setZoom(15);

      // Clear search input
      setSearchQuery('');
    });
  };

  // Handle manual search
  const handleSearch = () => {
    if (!mapInstance || !searchQuery.trim()) return;

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const place = results[0];
        
        // Clear previous search results
        searchResults.forEach(marker => marker.setMap(null));
        setSearchResults([]);

        // Create marker for searched location
        const searchMarker = new google.maps.Marker({
          position: place.geometry.location,
          map: mapInstance,
          title: place.formatted_address,
          animation: google.maps.Animation.DROP,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#3b82f6"/>
                <circle cx="12" cy="12" r="6" fill="white"/>
                <circle cx="12" cy="12" r="3" fill="#3b82f6"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(24, 24),
            anchor: new google.maps.Point(12, 24)
          }
        });

        // Create info window for search result
        const searchInfoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; min-width: 180px; font-family: system-ui, sans-serif;">
              <h3 style="margin: 0 0 6px 0; color: #1f2937; font-size: 14px; font-weight: 600;">
                ${place.formatted_address}
              </h3>
              <div style="margin-top: 8px;">
                <button onclick="window.openInGoogleMaps(${place.geometry.location.lat()}, ${place.geometry.location.lng()})" 
                        style="background: #3b82f6; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; margin-right: 6px;">
                  Open Maps
                </button>
                <button onclick="window.getDirections('${place.formatted_address}', ${place.geometry.location.lat()}, ${place.geometry.location.lng()})" 
                        style="background: #10b981; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px;">
                  Directions
                </button>
              </div>
            </div>
          `
        });

        // Add click listener to search marker
        searchMarker.addListener('click', () => {
          searchInfoWindow.open(mapInstance, searchMarker);
        });

        // Add to search results
        setSearchResults([searchMarker]);

        // Center map on searched location
        mapInstance.setCenter(place.geometry.location);
        mapInstance.setZoom(15);

        // Clear search input
        setSearchQuery('');
      } else {
        console.error('Geocoding failed:', status);
        alert('Location not found. Please try a different search term.');
      }
    });
  };

  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      try {
        if (!mapRef.current) {
          if (isMounted) {
            setError('Map container not available');
            setLoading(false);
          }
          return;
        }

        const apiKey = getGoogleMapsApiKey();
        if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
          if (isMounted) {
            setError('Google Maps API key not configured');
            setLoading(false);
          }
          return;
        }

        // Create a new loader instance for each map
        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['places']
        });

        const google = await loader.load();

        if (!isMounted || !mapRef.current) return;

        // Create map instance
        const map = new google.maps.Map(mapRef.current, {
          center: { lat: validLat, lng: validLng },
          zoom: hasValidCoordinates ? 15 : 12,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: 'greedy',
          clickableIcons: true,
          keyboardShortcuts: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Create marker
        const marker = new google.maps.Marker({
          position: { lat: validLat, lng: validLng },
          map: map,
          title: title,
          animation: google.maps.Animation.DROP,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#EC4899"/>
                <circle cx="12" cy="12" r="6" fill="white"/>
                <circle cx="12" cy="12" r="3" fill="#EC4899"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(24, 24),
            anchor: new google.maps.Point(12, 24)
          }
        });

        // Create info window
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; min-width: 180px; font-family: system-ui, sans-serif;">
              <h3 style="margin: 0 0 6px 0; color: #1f2937; font-size: 14px; font-weight: 600;">
                ${title}
              </h3>
              ${showFullAddress && address ? `
                <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 12px;">${address}</p>
              ` : ''}
              <div style="margin-top: 8px;">
                <button onclick="window.openInGoogleMaps(${validLat}, ${validLng})" 
                        style="background: #3b82f6; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; margin-right: 6px;">
                  Open Maps
                </button>
                <button onclick="window.getDirections('${address}', ${validLat}, ${validLng})" 
                        style="background: #10b981; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px;">
                  Directions
                </button>
              </div>
            </div>
          `
        });

        // Add click listener
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        // Open the info window by default if enabled
        if (openInfoByDefault) {
          infoWindow.open(map, marker);
        }

        if (isMounted) {
          setMapInstance(map);
          setLoading(false);
          
          // Initialize search functionality if enabled
          if (enableSearch && searchInputRef.current) {
            initializeSearch(map, google);
          }
        }

      } catch (err) {
        console.error('Error loading Google Maps:', err);
        if (isMounted) {
          setError(`Failed to load map: ${err instanceof Error ? err.message : 'Unknown error'}`);
          setLoading(false);
        }
      }
    };

    initMap();

    return () => {
      isMounted = false;
    };
  }, [validLat, validLng, title, address, showFullAddress, hasValidCoordinates, openInfoByDefault]);

  // Add global functions for info window buttons
  useEffect(() => {
    (window as any).openInGoogleMaps = (lat: number, lng: number) => {
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, '_blank');
    };

    (window as any).getDirections = (address: string, lat: number, lng: number) => {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&destination_place_id=${lat},${lng}`;
      window.open(url, '_blank');
    };

    return () => {
      delete (window as any).openInGoogleMaps;
      delete (window as any).getDirections;
    };
  }, []);

  return (
    <div className={`${className} ${height} relative rounded-lg overflow-hidden border border-gray-300`}>
      {/* Search Bar */}
      {enableSearch && (
        <div className="absolute top-4 left-4 right-4 z-10">
          <div className="bg-white rounded-lg shadow-lg p-2 flex items-center space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for a location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Search
            </button>
          </div>
        </div>
      )}
      
      <div ref={mapRef} className="w-full h-full" />

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 backdrop-blur-sm z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading map...</p>
            <p className="text-xs text-gray-500 mt-2">
              API Key: {getGoogleMapsApiKey() ? 'Present' : 'Missing'}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50/90 rounded-lg border border-red-200 z-10">
          <div className="text-center px-4">
            <div className="h-8 w-8 bg-red-500 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="text-sm text-red-600 mb-1">{error}</p>
            <p className="text-xs text-red-500">Please check your Google Maps API key</p>
            <p className="text-xs text-gray-500 mt-2">
              API Key: {getGoogleMapsApiKey() ? 'Present' : 'Missing'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleGoogleMap;
